import firebase from "@/libs/firebase.ts";
import timeSyncService from "@/libs/timeSync";
import { collection, doc, onSnapshot, query, where } from "firebase/firestore";
import { makeAutoObservable, runInAction } from "mobx";

export interface CreatorCallStatus {
  creatorId: string;
  available: boolean;
  /** True when creator has callStatus.available but is currently in a call */
  busy?: boolean;
  callType: "voice" | "video";
  voicePricePerMinute: number;
  videoPricePerMinute: number;
  updatedAt?: number;
}

export interface Call {
  callId: string;
  callerId: string;
  creatorId: string;
  callType: "voice" | "video";
  status: "pending" | "ringing" | "active" | "ended" | "missed" | "rejected";
  pricePerMinute: number;
  creatorStreamName?: string;
  userStreamName?: string;
  startedAt?: number;
  endedAt?: number;
}

export interface CallUrls {
  pushUrls: {
    rtmp?: string;
    webrtc?: string;
  };
  pullUrls: {
    webrtc?: string;
    hls?: string;
  };
  streamNames: {
    creator?: string;
    user?: string;
  };
}

/**
 * Call Store - manages available creators for calling and active calls
 * Uses Firestore listeners for real-time updates
 */
export class CallStore {
  // Available creators for calling
  availableCreators: Map<string, CreatorCallStatus> = new Map();
  loadingAvailable = true;

  // Current call state
  currentCall: Call | null = null;
  callUrls: CallUrls | null = null;
  isInCall = false;
  isConnecting = false;

  // Call ticket
  callTicketExpiry: number | null = null;

  // Online status map (from context)
  private onlineStatus: Record<string, number> = {};

  // Subscriptions
  private unsubscribeAvailable?: () => void;
  private unsubscribeCall?: () => void;
  /** Only treat as rejected if we had seen our callId in incomingCall (we were ringing). Avoids closing on accept when snapshot has stale/other onGoingCall. */
  private sawOurCallInIncomingCall = false;
  private isAdminUser = false;

  // Online status TTL (6 hours to match backend)
  private readonly ONLINE_TTL = 6 * 60 * 60 * 1000;

  constructor() {
    makeAutoObservable(this);
    this.subscribeToAvailableCreators();
  }

  setIsAdminUser(isAdminUser: boolean) {
    if (this.isAdminUser !== isAdminUser) {
      this.isAdminUser = isAdminUser;
      this.subscribeToAvailableCreators();
    }
  }

  /**
   * Update online status from context
   */
  setOnlineStatus(status: Record<string, number>) {
    this.onlineStatus = status;
  }

  /**
   * Check if a creator is online (lastActiveAt within TTL)
   * Uses server time for accurate comparison
   */
  isCreatorOnline(creatorId: string): boolean {
    const lastActiveAt = this.onlineStatus[creatorId];
    if (!lastActiveAt) {
      console.log(`[CallStore] Creator ${creatorId} is not online`);
      return false;
    }
    // Use server time for comparison
    const serverTimeMs = timeSyncService.getServerTimeSeconds() * 1000;
    return (serverTimeMs - lastActiveAt) < this.ONLINE_TTL;
  }

  /**
   * Get filtered list of available AND online creators
   */
  get availableAndOnlineCreators(): CreatorCallStatus[] {
    return Array.from(this.availableCreators.values()).filter(
      (creator) =>
        creator.available &&
        !creator.busy &&
        this.isCreatorOnline(creator.creatorId)
    );
  }

  /**
   * Subscribe to available creators (Firestore listener)
   * Listens to creators collection where callStatus.available == true
   */
  subscribeToAvailableCreators() {
    
    this.loadingAvailable = true;
    const db = firebase.firestore;
    const q = query(
      collection(db, "creators"),
      where("callStatus.available", "==", true)
    );

    this.unsubscribeAvailable?.();
    this.unsubscribeAvailable = onSnapshot(
      q,
      (snapshot) => {
        runInAction(() => {
          this.availableCreators.clear();
          snapshot.docs.forEach((doc) => {
            const data = doc.data();
            // Filter out admin creators if user is not admin
            if (!this.isAdminUser && (data as any).callStatus?.isAdmin === true) {
              return;
            }
            const callStatus = data.callStatus || {};
            const busy =
              (data as any).onGoingCall != null || (data as any).ongoingCallId;
            this.availableCreators.set(doc.id, {
              creatorId: doc.id,
              available: callStatus.available || false,
              busy: busy || undefined,
              callType: callStatus.callType || "video",
              voicePricePerMinute: callStatus.voicePricePerMinute || 500,
              videoPricePerMinute: callStatus.videoPricePerMinute || 1000,
              updatedAt: callStatus.updatedAt?.toMillis?.() || callStatus.updatedAt,
            });
          });
          this.loadingAvailable = false;
        });
      },
      (error) => {
        console.error("[CallStore] Error subscribing to available creators:", error);
        runInAction(() => {
          this.loadingAvailable = false;
        });
      }
    );
  }

  /**
   * Subscribe to creator doc to detect when call is rejected or ended by creator.
   * When we had seen ourselves in incomingCall (ringing) and then incomingCall is cleared
   * and we're not in onGoingCall, call onRejectedOrEnded.
   *
   * Only acts on server snapshots (not cache) and only treats as rejected if we had previously
   * seen our callId in incomingCall, so we don't close when creator accepts (stale/other onGoingCall).
   */
  subscribeToCall(callId: string, creatorId: string, onRejectedOrEnded: () => void) {
    this.unsubscribeCall?.();
    this.sawOurCallInIncomingCall = false;
    const db = firebase.firestore;
    const creatorRef = doc(db, "creators", creatorId);
    this.unsubscribeCall = onSnapshot(
      creatorRef,
      { includeMetadataChanges: true },
      (snapshot) => {
        if (!snapshot.exists()) return;
        if (snapshot.metadata.fromCache) return;
        const data = snapshot.data();
        const incomingCall = data?.incomingCall;
        const onGoingCall = data?.onGoingCall;
        // 1. Priority: onGoingCall (Active)
        if (onGoingCall?.callId === callId) {
          runInAction(() => {
            if (this.currentCall && this.currentCall.status !== "active") {
              this.currentCall.status = "active";
              this.isInCall = true;
            }
          });
          return;
        }

        // 2. Pending: incomingCall (don't downgrade from active — latch active to avoid flip from repeated snapshots)
        if (incomingCall?.callId === callId) {
          runInAction(() => {
            this.sawOurCallInIncomingCall = true;
            if (this.currentCall && this.currentCall.status !== "active") {
              this.currentCall.status = "pending";
            }
          });
          return;
        }

        // 3. Stale / Unknown
        if (onGoingCall === undefined) return;

        // 4. Rejected or Ended
        // If we were active, and now not in onGoingCall -> Ended
        // If we were pending (sawIncoming), and now not in incomingCall -> Rejected
        let shouldClose = false;
        if (this.currentCall?.status === "active") {
          console.log(`[CallStore] Closing active call: callId=${callId} not in onGoingCall (current=${onGoingCall?.callId})`);
          shouldClose = true;
        } else if (this.sawOurCallInIncomingCall) {
          console.log(`[CallStore] Closing pending call: callId=${callId} not in incomingCall (current=${incomingCall?.callId})`);
          shouldClose = true;
        }

        if (!shouldClose) return;

        this.unsubscribeCall?.();
        this.unsubscribeCall = undefined;
        runInAction(() => {
          this.currentCall = null;
          this.callUrls = null;
          this.isInCall = false;
          this.isConnecting = false;
          this.callTicketExpiry = null;
        });
        onRejectedOrEnded();
      },
      (err) => {
        console.error("[CallStore] Creator doc snapshot error:", err);
      }
    );
  }

  /**
   * Set call state after initiating
   */
  setCall(call: Call, urls?: CallUrls) {
    runInAction(() => {
      this.currentCall = call;
      this.callUrls = urls || null;
      this.isConnecting = false;
      this.isInCall = call.status === "active";
    });
  }

  /**
   * Set call URLs (e.g. after creator accepted). Also marks as in-call.
   */
  setCallUrls(urls: CallUrls) {
    runInAction(() => {
      this.callUrls = urls;
      this.isInCall = true;
    });
  }

  /**
   * Set connecting state
   */
  setConnecting(connecting: boolean) {
    runInAction(() => {
      this.isConnecting = connecting;
    });
  }

  /**
   * Set call ticket expiry
   */
  setTicketExpiry(expiry: number | null) {
    runInAction(() => {
      this.callTicketExpiry = expiry;
    });
  }

  /**
   * Cleanup after call ends
   */
  cleanup() {
    runInAction(() => {
      this.currentCall = null;
      this.callUrls = null;
      this.isInCall = false;
      this.isConnecting = false;
      this.callTicketExpiry = null;
    });
    this.unsubscribeCall?.();
    this.unsubscribeCall = undefined;
  }

  /**
   * Unsubscribe all
   */
  unsubscribe() {
    this.unsubscribeAvailable?.();
    this.unsubscribeAvailable = undefined;
    this.unsubscribeCall?.();
    this.unsubscribeCall = undefined;
  }
}

export const callStore = new CallStore();
